/**
 * Report Issue Modal Component
 * 
 * In-app feedback modal for beta testers to report issues with auto-attached
 * context data including device info, app version, and current state.
 * S-1006: Integrated with ModalManager for race condition prevention
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle, Bug, Zap, Palette, Lightbulb, HelpCircle, Send, X, CheckCircle } from 'lucide-react';
import { useModalManager, useModalFocus } from '@/hooks/useModalManager';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { getVersionForPayload } from '@/components/ui/AppFooter';
import { getEnabledFeatures } from '@/lib/featureFlags';

export interface ReportIssueModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Close modal callback */
  onClose: () => void;
  /** Current run ID (if applicable) */
  runId?: string;
  /** Current page context */
  currentPage?: string;
  /** Previous page context */
  previousPage?: string;
}

type FeedbackCategory = 'bug' | 'performance' | 'ui_ux' | 'feature_request' | 'other';

interface DeviceInfo {
  os: string;
  browser: string;
  device_model?: string;
  screen_resolution: string;
  viewport_size: string;
  user_agent: string;
}

interface AppInfo {
  version: string;
  build?: string;
  environment: string;
  feature_flags?: string[];
  current_page: string;
  previous_page?: string;
}

interface NetworkInfo {
  connection_type?: string;
  effective_type?: string;
  downlink?: number;
  rtt?: number;
}

const CATEGORY_OPTIONS = [
  {
    value: 'bug' as const,
    label: 'Bug Report',
    description: 'Something is broken or not working as expected',
    icon: Bug,
    color: 'text-red-600'
  },
  {
    value: 'performance' as const,
    label: 'Performance Issue',
    description: 'App is slow, unresponsive, or timing out',
    icon: Zap,
    color: 'text-orange-600'
  },
  {
    value: 'ui_ux' as const,
    label: 'UI/UX Issue',
    description: 'Design, layout, or usability problems',
    icon: Palette,
    color: 'text-blue-600'
  },
  {
    value: 'feature_request' as const,
    label: 'Feature Request',
    description: 'Suggestion for new functionality or improvements',
    icon: Lightbulb,
    color: 'text-green-600'
  },
  {
    value: 'other' as const,
    label: 'Other',
    description: 'General feedback or questions',
    icon: HelpCircle,
    color: 'text-gray-600'
  }
];

export function ReportIssueModal({
  isOpen,
  onClose,
  runId,
  currentPage = window.location.pathname,
  previousPage
}: ReportIssueModalProps) {
  
  // S-1006: Integrate with ModalManager for race condition prevention
  const modalId = `feedback-${runId || 'general'}-${Date.now()}`;
  const modalManager = useModalManager({
    modalId,
    type: 'feedback',
    priority: 5, // Medium priority for feedback modals
    canStack: true, // Feedback modals can stack with payments
    blockingLevel: 'none', // Don't block other modals
    onClose,
    metadata: { runId, currentPage, previousPage }
  });

  const { modalRef } = useModalFocus(modalId, modalManager.isOpen);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [includeSystemInfo, setIncludeSystemInfo] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [appInfo, setAppInfo] = useState<AppInfo | null>(null);
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo | null>(null);
  const [sessionInfo, setSessionInfo] = useState({
    session_duration: 0,
    actions_taken: 0,
    timestamp: new Date().toISOString()
  });

  // Sync external isOpen prop with ModalManager
  useEffect(() => {
    if (isOpen && !modalManager.isOpen) {
      modalManager.open();
    } else if (!isOpen && modalManager.isOpen) {
      modalManager.close();
    }
  }, [isOpen, modalManager]);

  /**
   * Collect device and system information
   */
  useEffect(() => {
    if (!modalManager.isOpen) return;

    // Collect device info
    const collectDeviceInfo = (): DeviceInfo => {
      const nav = navigator as any;
      
      return {
        os: getOperatingSystem(),
        browser: getBrowserInfo(),
        device_model: nav.userAgentData?.platform || 'unknown',
        screen_resolution: `${screen.width}x${screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        user_agent: navigator.userAgent
      };
    };

    // Collect app info
    const collectAppInfo = (): AppInfo => {
      const versionInfo = getVersionForPayload();
      return {
        version: versionInfo.app_version,
        build: versionInfo.build_number,
        environment: versionInfo.environment,
        feature_flags: getActiveFeatureFlags(),
        current_page: currentPage,
        previous_page: previousPage
      };
    };

    // Collect network info
    const collectNetworkInfo = (): NetworkInfo => {
      const nav = navigator as any;
      const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
      
      if (connection) {
        return {
          connection_type: connection.type || 'unknown',
          effective_type: connection.effectiveType || 'unknown',
          downlink: connection.downlink,
          rtt: connection.rtt
        };
      }
      
      return {};
    };

    // Collect session info
    const collectSessionInfo = () => {
      const sessionStart = sessionStorage.getItem('session_start');
      const actionsCount = sessionStorage.getItem('actions_count');
      
      setSessionInfo({
        session_duration: sessionStart ? Date.now() - parseInt(sessionStart) : 0,
        actions_taken: actionsCount ? parseInt(actionsCount) : 0,
        timestamp: new Date().toISOString()
      });
    };

    setDeviceInfo(collectDeviceInfo());
    setAppInfo(collectAppInfo());
    setNetworkInfo(collectNetworkInfo());
    collectSessionInfo();

  }, [modalManager.isOpen, currentPage, previousPage]);

  /**
   * Get operating system from user agent
   */
  const getOperatingSystem = (): string => {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Mac OS X')) {
      const match = userAgent.match(/Mac OS X ([0-9_]+)/);
      return match ? `macOS ${match[1].replace(/_/g, '.')}` : 'macOS';
    }
    if (userAgent.includes('Windows NT')) {
      const match = userAgent.match(/Windows NT ([0-9.]+)/);
      return match ? `Windows ${match[1]}` : 'Windows';
    }
    if (userAgent.includes('iPhone')) {
      const match = userAgent.match(/OS ([0-9_]+)/);
      return match ? `iOS ${match[1].replace(/_/g, '.')}` : 'iOS';
    }
    if (userAgent.includes('Android')) {
      const match = userAgent.match(/Android ([0-9.]+)/);
      return match ? `Android ${match[1]}` : 'Android';
    }
    if (userAgent.includes('Linux')) return 'Linux';
    
    return 'Unknown';
  };

  /**
   * Get browser information
   */
  const getBrowserInfo = (): string => {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      const match = userAgent.match(/Chrome\/([0-9.]+)/);
      return match ? `Chrome ${match[1]}` : 'Chrome';
    }
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      const match = userAgent.match(/Version\/([0-9.]+)/);
      return match ? `Safari ${match[1]}` : 'Safari';
    }
    if (userAgent.includes('Firefox')) {
      const match = userAgent.match(/Firefox\/([0-9.]+)/);
      return match ? `Firefox ${match[1]}` : 'Firefox';
    }
    if (userAgent.includes('Edg')) {
      const match = userAgent.match(/Edg\/([0-9.]+)/);
      return match ? `Edge ${match[1]}` : 'Edge';
    }
    
    return 'Unknown';
  };

  /**
   * Get active feature flags
   */
  const getActiveFeatureFlags = (): string[] => {
    return getEnabledFeatures();
  };

  /**
   * Submit feedback to API
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      // Prepare payload
      const payload = {
        title: title.trim(),
        description: description.trim(),
        category,
        run_id: runId,
        context: {
          device_info: deviceInfo!,
          app_info: appInfo!,
          network_info: networkInfo || {},
          session_info: sessionInfo,
          error_logs: getRecentErrorLogs()
        }
      };

      // Submit directly to Supabase
      const { data, error } = await supabase
        .from('beta_feedback')
        .insert({
          user_id: session.user.id,
          run_id: payload.run_id,
          payload: payload,
          category: payload.category,
          priority: 'P2', // Default priority
          status: 'open'
        })
        .select()
        .single();

      if (error) {
        throw new Error(error.message || 'Failed to submit feedback');
      }

      setSubmitSuccess(true);
      
      // Reset form after short delay
      setTimeout(() => {
        setTitle('');
        setDescription('');
        setCategory('bug');
        setSubmitSuccess(false);
        modalManager.close();
      }, 2000);

    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Get recent error logs from console or storage
   */
  const getRecentErrorLogs = () => {
    // This would integrate with your error logging system
    const logs = [];
    
    // Check for stored error logs
    const storedLogs = localStorage.getItem('error_logs');
    if (storedLogs) {
      try {
        const parsed = JSON.parse(storedLogs);
        logs.push(...parsed.slice(-5)); // Last 5 errors
      } catch (e) {
        // Ignore parsing errors
      }
    }
    
    return logs;
  };

  /**
   * Reset modal state when closed
   */
  useEffect(() => {
    if (!modalManager.isOpen) {
      setSubmitSuccess(false);
      setSubmitError(null);
    }
  }, [modalManager.isOpen]);

  const selectedCategoryOption = CATEGORY_OPTIONS.find(opt => opt.value === category);

  const handleClose = () => {
    modalManager.close();
  };

  return (
    <Dialog open={modalManager.isOpen} onOpenChange={handleClose}>
      <DialogContent 
        ref={modalRef}
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
        style={{ zIndex: modalManager.zIndex }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600" />
            Report an Issue
          </DialogTitle>
          <DialogDescription>
            Help us improve by reporting bugs, performance issues, or sharing feedback.
            Your device and app information will be automatically included to help us debug.
          </DialogDescription>
        </DialogHeader>

        {submitSuccess ? (
          <div className="py-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-700 mb-2">
              Feedback Submitted Successfully!
            </h3>
            <p className="text-sm text-muted-foreground">
              Thank you for helping us improve. We'll review your feedback and get back to you if needed.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category Selection */}
            <div className="space-y-3">
              <Label>What type of issue are you reporting?</Label>
              <RadioGroup value={category} onValueChange={(value) => setCategory(value as FeedbackCategory)}>
                {CATEGORY_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <div key={option.value} className="flex items-center space-x-2">
                      <RadioGroupItem value={option.value} id={option.value} />
                      <Label htmlFor={option.value} className="flex items-center gap-2 cursor-pointer flex-1">
                        <Icon className={`h-4 w-4 ${option.color}`} />
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-sm text-muted-foreground">{option.description}</div>
                        </div>
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">
                Issue Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief description of the issue"
                maxLength={100}
                required
              />
              <div className="text-xs text-muted-foreground">
                {title.length}/100 characters
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">
                Detailed Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please describe what happened, what you expected to happen, and steps to reproduce the issue..."
                rows={4}
                maxLength={1000}
                required
              />
              <div className="text-xs text-muted-foreground">
                {description.length}/1000 characters
              </div>
            </div>

            {/* System Info Preview */}
            {deviceInfo && appInfo && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>System Information</Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="include-system-info"
                      checked={includeSystemInfo}
                      onCheckedChange={setIncludeSystemInfo}
                    />
                    <Label htmlFor="include-system-info" className="text-sm">
                      Include system info
                    </Label>
                  </div>
                </div>
                
                {includeSystemInfo && (
                  <div className="bg-muted/30 p-3 rounded-lg space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">{deviceInfo.os}</Badge>
                      <Badge variant="outline">{deviceInfo.browser}</Badge>
                      <Badge variant="outline">v{appInfo.version}</Badge>
                      {runId && <Badge variant="outline">Run: {runId.slice(0, 8)}...</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Screen: {deviceInfo.screen_resolution} | 
                      Viewport: {deviceInfo.viewport_size} | 
                      Page: {appInfo.current_page}
                      {networkInfo?.effective_type && ` | Network: ${networkInfo.effective_type}`}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error Display */}
            {submitError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  {submitError}
                </AlertDescription>
              </Alert>
            )}

            {/* Submit Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !title.trim() || !description.trim()}>
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Feedback
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ReportIssueModal;
